import type {
  TBulkSimulateApplicationInputDTO,
  TSimulateApplicationOutputDTO,
  TSimulationMode,
} from '@doutorsim/shared/applications'
import { useMutation } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { mutations } from '../../_lib/react-query'
import { SimulationForm } from '../_components/simulation-form'
import { SimulationHeader } from '../_components/simulation-header'
import { SimulationResultsPanel } from '../_components/simulation-results-panel'

export const SimulationScene = () => {
  const [simulationData, setSimulationData] = useState<TBulkSimulateApplicationInputDTO | null>(
    null,
  )
  const [simulationMode, setSimulationMode] = useState<TSimulationMode>('product_cost')
  const [selectedInstallment, setSelectedInstallment] =
    useState<TSimulateApplicationOutputDTO | null>(null)
  const [installments, setInstallments] = useState<TSimulateApplicationOutputDTO[]>([])

  const simulateApplication = useMutation(mutations.applications.bulkSimulate())
  const isLoading = simulateApplication.isPending

  const prevSimKey = useRef<string | null>(null)

  useEffect(() => {
    if (!simulationData) return

    const simKey = `${simulationData.requestedAmount}-${simulationData.firstPaymentDate}-${simulationMode}`
    if (simKey === prevSimKey.current) return
    prevSimKey.current = simKey

    simulateApplication.mutate(
      { ...simulationData, mode: simulationMode },
      {
        onSuccess: (installmentOptions) => {
          if (installmentOptions.length === 0) return

          setInstallments(installmentOptions)

          const targetMonth = selectedInstallment?.numPayments ?? 12
          const defaultInstallment =
            installmentOptions.find((i) => i.numPayments === targetMonth) ?? installmentOptions[0]
          if (defaultInstallment) setSelectedInstallment(defaultInstallment)
        },
        onError: (error) => {
          toast.error(
            'Não foi possível recuperar as parcelas disponíveis. Por favor, tente novamente.',
          )
          console.error(error)
        },
      },
    )
  }, [simulationData, simulationMode])

  const handleSimulate = useCallback(
    (data: TBulkSimulateApplicationInputDTO, mode: TSimulationMode) => {
      setSimulationData(data)
      setSimulationMode(mode)
    },
    [],
  )

  const handleInstallmentSelection = useCallback((installment: TSimulateApplicationOutputDTO) => {
    setSelectedInstallment(installment)
  }, [])

  return (
    <div className="h-full overflow-y-scroll bg-brand-grayscale-0">
      <SimulationHeader />
      <div className="mx-auto max-w-6xl px-14 pb-10">
        <div className="grid grid-cols-1 overflow-hidden rounded-xl shadow-[0_2px_8px_-2px_rgba(0,0,0,0.12)] lg:grid-cols-2">
          <div className="bg-brand-grayscale-2 p-8 lg:p-10 lg:pr-12">
            <SimulationForm onSimulate={handleSimulate} />
          </div>

          <SimulationResultsPanel
            simulationData={simulationData}
            installments={installments}
            selectedInstallment={selectedInstallment}
            isLoading={isLoading}
            onInstallmentSelection={handleInstallmentSelection}
          />
        </div>
      </div>
    </div>
  )
}
